import { ReactElement, useEffect } from 'react';
import { useFormikContext, Field, getIn } from 'formik';
import { FormType } from '@constants/forms';
import { Alert, MultiTextInput, TileRadio } from '@components';
import { tile } from '@interfaces/interfaces';
import { iFVSFormValues } from '@services/FVSService';
import InfoIcon from '../../../../images/info.svg';
import styles from './FVSStep18.module.scss';

function FVSStep18(): ReactElement {
  const { values }: { values: iFVSFormValues } = useFormikContext();
  const formikContext = useFormikContext();

  const FVSStep18AlertContent = (): ReactElement => (
    <p>
      If any of the fines are on a payment plan with an enforcement agency (eg. Victoria Police, Public Transport Victoria) you should contact them directly to cancel the plan – if
      the fineholder pays the fine in full we cannot consider it in the application.
    </p>
  );

  const YesValue = (): ReactElement => (
    <>
      <div>Yes</div>
    </>
  );

  const NoValue = (): ReactElement => (
    <>
      <div>No</div>
    </>
  );

  const EligibleFinesValue = (): ReactElement => (
    <>
      <div>All eligible outstanding fines</div>
    </>
  );

  const FollowingEligibleFines = (): ReactElement => (
    <>
      <div>The following eligible outstanding fines (please list below)</div>
    </>
  );

  const ExcludingEligibleFines = (): ReactElement => (
    <>
      <div>
        All eligible outstanding fines
        <span className={styles.bold}> EXCEPT </span>
        these fines (please list below)
      </div>
    </>
  );

  const REGISTERED_FINE_TYPE_DATA: tile[] = [
    {
      body: <EligibleFinesValue />,
      value: 'All eligible outstanding fines',
    },
    {
      body: <FollowingEligibleFines />,
      value: 'The following eligible outstanding fines (please list below)',
    },
    {
      body: <ExcludingEligibleFines />,
      value: 'All eligible outstanding fines EXCEPT these fines (please list below)',
    },
  ];

  const ADDITIONAL_FINES_CONFIRMATION_DATA: tile[] = [
    {
      body: <YesValue />,
      value: 'true',
    },
    {
      body: <NoValue />,
      value: 'false',
    },
  ];

  useEffect(() => {
    /* Condition 1 */
    if (getIn(values, 'AdditionalFinesType') === 'All eligible outstanding fines' || getIn(values, 'RegisteredFinesToInclude') === 'false') {
      formikContext.setFieldValue('obligations', [{ value: '' }]);
      formikContext.setFieldTouched('obligations');
    }

    /* Condition 2 */
    if (getIn(values, 'InfringementFinesToInclude') !== 'true') {
      formikContext.setFieldValue('AgencyNames', [{ value: '' }]);
      formikContext.setFieldValue('InfringementNums', [{ value: '' }]);
      formikContext.setFieldValue('AOffence', [{ value: '' }]);
      formikContext.setFieldValue('DAOffence', [{ value: '' }]);
      formikContext.setFieldTouched('AgencyNames');
      formikContext.setFieldTouched('InfringementNums');
      formikContext.setFieldTouched('AOffence');
      formikContext.setFieldTouched('DAOffence');
    }
  }, [getIn(values, 'RegisteredFinesToInclude'), getIn(values, 'InfringementFinesToInclude')]);

  return (
    <>
      <div className="vfH2">Fines to be included in this application</div>
      <span className="subHeadingH3">If the fineholder has been issued with other eligible fines, we may be able to consider them as a part of this application</span>
      <p>You may not add:</p>
      <ul>
        <li>fines issued by a court</li>
        <li>fully-paid fines</li>
        <li>fines for drink/drug driving or excessive speed</li>
        <li>fines for which you have received a 7 Day Notice that has expired</li>
      </ul>
      <div className={styles.offenceDetails}>
        <div className="vfH3">Registered fines</div>
        <p className={styles.middleNoteSection}>Registered fines are fines for which you have received a Notice of Final Demand from Fines Victoria.</p>
        <div>
          <Field
            name="RegisteredFinesToInclude"
            title="Would you like to add other registered fines to this application?"
            component={TileRadio}
            tileData={ADDITIONAL_FINES_CONFIRMATION_DATA}
            orientationRow
            isMandatory
          />
        </div>
        {values.RegisteredFinesToInclude === 'true' && (
          <div>
            <Field
              name="AdditionalFinesType"
              title="Which registered fines would you like to add?"
              component={TileRadio}
              tileData={REGISTERED_FINE_TYPE_DATA}
              isMandatory
              classname={styles.tile}
            />
          </div>
        )}
        {values.RegisteredFinesToInclude === 'true' && values.AdditionalFinesType !== 'All eligible outstanding fines' && values.AdditionalFinesType !== '' && (
          <MultiTextInput name="obligations" formName={FormType.FAMILYVIOLENCESCHEME} stepNo="step18" includeRegisteredFines />
        )}
        <br />
        <div className="vfH3">Infringement stage fines</div>
        <p className={styles.middleNoteSection}>
          FVS can also consider any fines for which you have not yet received a notice of final demand and therefore are still at infringement stage, however we require more
          information from you to identify these fines.
        </p>
        <div>
          <Field
            name="InfringementFinesToInclude"
            title="Does the fineholder have any infringement stage fines that you would like us to consider?"
            component={TileRadio}
            tileData={ADDITIONAL_FINES_CONFIRMATION_DATA}
            orientationRow
            isMandatory
          />
        </div>
        <br />
        {values.InfringementFinesToInclude === 'true' && (
          <>
            <span className="vfH3">Please enter as much detail as possible to help speed up the application.</span>
            <MultiTextInput name="infringementFineArray" formName={FormType.FAMILYVIOLENCESCHEME} stepNo="step18" includeUnregisteredFines />
          </>
        )}
        {values.InfringementFinesToInclude === 'true' && <Alert message={<FVSStep18AlertContent />} theme="info" title="Please note:" icon={InfoIcon} />}
      </div>
    </>
  );
}

export default FVSStep18;
